import { useCallback } from 'react';

import { BANNER_IMAGE_CROP_DEFAULT } from 'utils/constants/banner';
import {
    CAMPAIGN_CREATING_STATUSES,
    CampaignProcess,
    CampaignType,
    isFirstStep,
    STEPS_COUNT_BY_PROCESS,
} from 'utils/constants/campaign';
import { Settings } from 'utils/constants/dynamicSettings';

import { useDashboardRoute } from 'hooks/useDashboardRoute';
import useSettings from 'hooks/useSettings';
import { useGetValidateErrors } from 'utils/campaign/hooks/useGetValidateErrors';

import { setNextStepForCampaign } from 'components/CpcFlexibleCreate/CpcActions/setNextStepForCampaign';
import { setNextStepForCampaignAndBanner } from 'components/CpcFlexibleCreate/CpcActions/setNextStepForCampaignAndBanner';
import { setNextStepForCopy } from 'components/CpcFlexibleCreate/CpcActions/setNextStepForCopy';

import { selectIsAdvertisement } from 'store/models/createBanner';
import { selectCampaignProcess, setCampaignErrors } from 'store/models/createCampaign';
import { setIsGalleryModalVisible } from 'store/models/gallery';
import { setRequisitesFormOpen, setWrongRequisitesModalOpen } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

type UseCpcNextStep = (setSaving: (isSaving: boolean) => void) => () => void;

export const useCpcNextStep: UseCpcNextStep = (setSaving) => {
    const { currentStep, campaignType, campaignErrors } = useSelector((state) => state.createCampaign);
    const campaignProcess = useSelector(selectCampaignProcess);
    const { images, bannerLink } = useSelector((state) => state.createBanner);
    const isRequisitesFilled = useSelector((state) => state.requisites.isRequisitesFilled);
    const isAdvertisement = useSelector(selectIsAdvertisement);

    const dispatch = useDispatch();
    const settings = useSettings();
    const dashboardRoute = useDashboardRoute();
    const validateErrors = useGetValidateErrors();
    const isInBlocklistCampaignCreating = settings(Settings.BlocklistCampaignCreating);

    const formCountSteps = STEPS_COUNT_BY_PROCESS[campaignProcess] - 1;
    const isLastStep = formCountSteps === currentStep;
    const isCampaignCreatingProcess = CAMPAIGN_CREATING_STATUSES.includes(campaignProcess);

    const needFixWrongRequisites =
        isCampaignCreatingProcess &&
        isFirstStep(currentStep) &&
        campaignType === CampaignType.Advertising &&
        !!bannerLink &&
        isInBlocklistCampaignCreating;

    const needFilledRequisites =
        isCampaignCreatingProcess &&
        isFirstStep(currentStep) &&
        !isRequisitesFilled &&
        ((campaignType === CampaignType.Advertising && !!bannerLink) || isAdvertisement);

    const needFilledTotalBudget = isLastStep && validateErrors?.adTotalBudgetNoLimits;

    const onNextStepClick = useCallback(() => {
        if (needFixWrongRequisites) {
            dispatch(setWrongRequisitesModalOpen(true));
            return;
        }

        if (needFilledRequisites) {
            dispatch(setRequisitesFormOpen(true));
            return;
        }

        if (needFilledTotalBudget) {
            dispatch([
                setCampaignErrors({
                    ...campaignErrors,
                    ...validateErrors,
                }),
            ]);
            return;
        }

        if (isLastStep) {
            setSaving(true);
        }

        if (isCampaignCreatingProcess && isFirstStep(currentStep) && !images[BANNER_IMAGE_CROP_DEFAULT].id) {
            dispatch(setIsGalleryModalVisible(true));
        } else if (campaignProcess === CampaignProcess.Edit) {
            void dispatch(setNextStepForCampaign(currentStep, setSaving, formCountSteps, dashboardRoute));
        } else if (campaignProcess === CampaignProcess.Copy) {
            void dispatch(setNextStepForCopy(currentStep, setSaving, dashboardRoute));
        } else {
            void dispatch(setNextStepForCampaignAndBanner(currentStep, setSaving, dashboardRoute));
        }
    }, [
        needFixWrongRequisites,
        needFilledRequisites,
        needFilledTotalBudget,
        isLastStep,
        isCampaignCreatingProcess,
        currentStep,
        images,
        campaignProcess,
        setSaving,
        formCountSteps,
        dashboardRoute,
        dispatch,
        campaignErrors,
        validateErrors,
    ]);

    return onNextStepClick;
};
